import {useEffect, useEffectEvent, useRef} from 'react'

import {CommandEvent, type CommandEventHandlersMap} from '../command-event'
import type {CommandId} from '../commands'
import {getAllRegisteredGlobalCommands, getGlobalKeybindingMap, useRegisterCommands} from '../commands-registry'
import {recordCommandTriggerEvent} from '../metrics'
import {useDetectConflicts} from '../use-detect-conflicts'
import {createKeyDownHandler} from '../use-key-down-handler'
import {getActiveModal, isInsideModal} from '../utils'

export interface GlobalCommandsProps {
  /** Map of command IDs to the corresponding event handler. */
  commands: CommandEventHandlersMap
}

class UiCommandTriggerEvent extends Event {
  commandId: CommandId
  domEvent: KeyboardEvent | MouseEvent
  constructor(commandId: CommandId, domEvent: KeyboardEvent | MouseEvent) {
    super(customDomEventName)
    this.commandId = commandId
    this.domEvent = domEvent
  }
}

/**
 * There's no context for global commands because they can be defined in any react app on the page. So to be able to
 * trigger them without keyboard events, we emit and listen for custom DOM events instead.
 */
const customDomEventName = 'ui-command-trigger'

/** Trigger a global command without a keyboard event. */
export function dispatchGlobalCommand(commandId: CommandId, domEvent: KeyboardEvent | MouseEvent) {
  document.dispatchEvent(new UiCommandTriggerEvent(commandId, domEvent))
}

const triggerCommand = <T extends CommandId>(commandId: T, domEvent: KeyboardEvent | MouseEvent) => {
  const handler = getAllRegisteredGlobalCommands()[commandId]

  if (handler) {
    const event = new CommandEvent(commandId, domEvent)
    try {
      handler(event)
    } finally {
      recordCommandTriggerEvent(event, domEvent)
    }
  } else {
    // Return false to indicate the command was not handled, allowing the event to propagate
    // and the browser default behavior to occur (e.g., Cmd+S to save the page)
    return false
  }
}

/** Elements rendered by each mounted `GlobalCommands`, used to determine whether any of them is inside the active modal. */
const mountedElements = new Set<HTMLElement>()

// Created on first mount and dropped on last unmount, so sequence state and the last event aren't retained
let globalKeyDownHandler: ReturnType<typeof createKeyDownHandler> | null = null

/**
 * When a modal dialog is open, global commands should not be able to trigger 'underneath' that dialog. This prevents
 * unexpected things from happening in the page behind the active modal. For example, you shouldn't be able to navigate
 * the underlying page when there is a dialog taking over the whole view. Commands can still trigger if any
 * `GlobalCommands` is rendered inside the active modal.
 */
function isBlockedByModal() {
  const activeModal = getActiveModal()
  if (!activeModal) return false

  for (const element of mountedElements) if (isInsideModal(activeModal, element)) return false
  return true
}

/**
 * All mounted `GlobalCommands` share one document listener. The modal check can force a layout read, so it only runs
 * once a keypress matches, or is the start of, a registered command's keybinding.
 */
function onDocumentKeyDown(event: KeyboardEvent) {
  globalKeyDownHandler?.(event, getGlobalKeybindingMap, triggerCommand)
}

function registerKeyDownListener(element: HTMLElement) {
  if (mountedElements.size === 0) {
    globalKeyDownHandler = createKeyDownHandler({isBlocked: isBlockedByModal})
    document.addEventListener('keydown', onDocumentKeyDown)
  }
  mountedElements.add(element)

  return () => {
    mountedElements.delete(element)
    if (mountedElements.size === 0) {
      document.removeEventListener('keydown', onDocumentKeyDown)
      globalKeyDownHandler = null
    } else {
      // Drop any partly typed sequence, so keys typed in a removed instance (e.g. a modal) can't be completed later
      globalKeyDownHandler = createKeyDownHandler({isBlocked: isBlockedByModal})
    }
  }
}

/**
 * Provide command handlers that are activatable when focus is anywhere on the current page, including outside this
 * React app.
 *
 * @example
 * <GlobalCommands commands={{'issues:navigateToCode': navigateToCode}} />
 */
export const GlobalCommands = ({commands}: GlobalCommandsProps) => {
  const elementRef = useRef<HTMLDivElement>(null)

  const onCustomEvent = useEffectEvent((event: Event) => {
    if (!isValidCommandEvent(event) || wasAlreadyHandled(event)) return

    const {commandId, domEvent} = event
    triggerCommand(commandId, domEvent)
  })

  useDetectConflicts('global', commands)

  useRegisterCommands('global', commands)

  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    const unmountController = new AbortController()
    document.addEventListener(customDomEventName, onCustomEvent, {signal: unmountController.signal})
    const unregisterKeyDownListener = registerKeyDownListener(element)

    return () => {
      unmountController.abort()
      unregisterKeyDownListener()
    }
  }, [])

  return <div ref={elementRef} className="d-none" />
}

function isValidCommandEvent(event: Event): event is UiCommandTriggerEvent {
  return event instanceof UiCommandTriggerEvent
}

const handledEvents = new WeakMap<Event, true>()

/**
 * Every mounted `GlobalCommands` listens for the custom trigger event. To avoid handling the same event twice, the
 * first handler to see it records it and subsequent handlers completely ignore recorded events. The record is kept in a
 * WeakMap keyed on the event rather than mutating the browser-owned event; the entry is released once the event is
 * garbage-collected.
 */
function wasAlreadyHandled(event: Event): boolean {
  if (handledEvents.has(event)) {
    return true
  }

  // Record the event as handled so that if another GlobalCommands component receives it, it will know not to handle it again
  handledEvents.set(event, true)
  return false
}
